import {put, call} from 'redux-saga/effects';
import Transaction from "../../entities/transaction";
import * as Actions from "../actions/transactionActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type TransactionAction = {
    transactionId: string;
    transaction: Transaction;
    transactions: Transaction[];
};

export function* fetchOneTransaction(action: TransactionAction) {
	try {
		const transaction: Transaction = yield call(api.findOne, 'transaction', action.transactionId);

		// Now we can update the user interface
		yield put({type: Actions.TRANSACTION_FETCH_ONE_SUCCESS, transactionId: action.transactionId, transaction});
	}
	catch(err) {
		yield put({type: Actions.TRANSACTION_FETCH_ONE_FAILURE, transactionId: action.transactionId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {TransactionAction} action
 */
export function* saveTransaction(action: TransactionAction) {
    // console.debug('⮂ TRANSACTION_SAVE saga', action.transaction?.id);
    
	if (action.transaction === undefined || action.transaction === null) {
		console.error('saveTransaction received a null');
		throw new Error('saveTransaction received a null');
	}

	if (!(action.transaction instanceof Transaction)) {
		action.transaction = new Transaction(action.transaction);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'transaction', action.transaction);
		if (addResult.isErr()) {
			console.error(`⮂ saveTransaction: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TRANSACTION_SAVE_FAILURE, transaction: action.transaction, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TRANSACTION_SAVE, transaction: action.transaction});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'transaction', action.transaction.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.TRANSACTION_SAVE_FAILURE, transaction: action.transaction, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {TransactionAction} action
 * @param {Transaction[]} action.transactions
 */
export function* saveManyTransactions(action: TransactionAction) {
    // console.debug('⮂ TRANSACTION_SAVE_MANY saga', action.transactions.map(transaction => transaction.id).join(','));
    
    if (!Array.isArray(action.transactions)) {
        console.error('saveManyTransactions expected an array, received', action.transactions);
        throw new Error('saveManyTransactions expected an array');
    }

	try {
		const toSave: Transaction[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.transactions.length; i++) {
			const transaction = action.transactions[i];
			transaction.persisted = false;
			toSave.push(transaction);
		}

		const addResult = yield call(idb.add, 'transaction', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyTransactions: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TRANSACTION_SAVE_MANY_FAILURE, transactions: action.transactions, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TRANSACTION_SAVE_MANY, transactions: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.TRANSACTION_SAVE_MANY_FAILURE, transactions: action.transactions, status: 500, err});

		console.error(`Error saving ${action.transactions.length} transactions to database`, err);
	}
}

/**
 * @param {TransactionAction} action
 */
export function* removeTransaction(action: TransactionAction) {
    const userId = getCurrentUserId();
	if (!userId || action.transaction === undefined || action.transaction === null || !(action.transaction instanceof Transaction)) {
			console.error('removeTransaction expected type Transaction, received', action.transaction);
			throw new Error('removeTransaction expected type Transaction');
		}

	try {
		// Mark as deleted
		action.transaction.status = 'D';
		action.transaction.addedOn = newHRTimestamp();
		action.transaction.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'transaction', action.transaction);
		if (addResult.isErr()) {
			console.error(`⮂ removeTransaction: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TRANSACTION_REMOVE_FAILURE, transaction: action.transaction, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TRANSACTION_REMOVE, transaction: action.transaction});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.TRANSACTION_REMOVE_FAILURE, transaction: action.transaction, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Transaction.
	*
	* @param {TransactionAction} action
	*/
export function* restoreTransaction(action: TransactionAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreTransaction expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.transaction === undefined || action.transaction === null) {
        console.error('restoreTransaction expected a Transaction, received null');
        throw new Error('restoreTransaction expected a Transaction, received null');
    }
    else if (!(action.transaction instanceof Transaction)) action.transaction = new Transaction(action.transaction); 
    
	try {
		// Mark as restored
		action.transaction.status = 'U';
		action.transaction.addedOn = newHRTimestamp();
		action.transaction.addedBy = userId;
		action.transaction.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'transaction', action.transaction);
		if (addResult.isErr()) {
			console.error(`⮂ restoreTransaction: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TRANSACTION_RESTORE_FAILURE, transaction: action.transaction, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TRANSACTION_RESTORE, transaction: action.transaction});
	}
	catch(err) {
		yield put({type: Actions.TRANSACTION_RESTORE_FAILURE, transaction: action.transaction, status: 500, err});

		console.error('Error restoring from database', err);
	}
}